'use client';

import { c as _c } from "react/compiler-runtime";
import { useResultListContext } from '../_results/ResultList';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { FilteredMarkupAttributes } from '@/sections/markup/helpers/filterAttributes.helpers';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { ULMarker } from '@/uiComposites/list/ListItem';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { stack } from '@/uiPrimitives/layout/stack';
import { getQuestionMethods } from '../_questions/getQuestionMethods';
import { boldUnlessHasBoldChildren } from './AnswerList.css';
import { createElement } from 'react';
export type AnswerListData = {
  matrix?: {
    [QuestionId: string]: {
      columnIds: Array<string>;
    } | undefined;
  };
  single?: {
    [QuestionId: string]: true | undefined;
  };
  multiple?: {
    [QuestionId: string]: true | undefined;
  };
};
const parseAnswerListData = (jsonString: string | undefined): AnswerListData | undefined => {
  if (jsonString == null) return undefined;
  try {
    return JSON.parse(jsonString);
  } catch (_error) {
    return undefined;
  }
};
const useAnswerList = json => {
  const $ = _c(2);
  const answerListData = parseAnswerListData(json);
  const {
    givenAnswers,
    relevantQuestions
  } = useResultListContext();
  if (answerListData == null) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = [];
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (relevantQuestions.length === 0) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = [];
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  const items = relevantQuestions.flatMap(question => {
    const {
      getAnswerListItems
    } = getQuestionMethods(question.type);
    return getAnswerListItems({
      question,
      givenAnswers,
      answerListData
    });
  });
  return items;
};
export const AnswerListClient = t0 => {
  const $ = _c(15);
  const {
    attributes,
    headingLevel
  } = t0;
  const answerItems = useAnswerList(attributes["data-answers"]);
  if (answerItems.length === 0) {
    const answerListFallbackText = attributes["data-fallback-text"];
    if (answerListFallbackText == null) {
      return null;
    }
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = classes(textBlock({
        fontWeight: "bold",
        color: "positiveGreen"
      }));
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== answerListFallbackText || $[2] !== attributes || $[3] !== t1) {
      t2 = <p {...attributes} className={t1}>{answerListFallbackText}</p>;
      $[1] = answerListFallbackText;
      $[2] = attributes;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classes(stack({
      rowGap: "4 | betweenListItems"
    }), textStyle({
      capLineHeight: "tight"
    }));
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== answerItems || $[7] !== headingLevel) {
    let t3;
    if ($[9] !== headingLevel) {
      t3 = (item, index) => <li key={index} className={classes(shelf({
        alignItems: "start"
      }))}><span className={classes(occupy({
          blockSize: "capSize",
          inlineSize: indentSize,
          justifyItems: "start"
        }), box({
          paddingInlineEnd: "-1 | small"
        }))}><ULMarker /></span><span className={classes(stack({
          rowGap: "1 | h3"
        }))}>{createElement(headingLevel, {
            className: classes(textBlock({}), boldUnlessHasBoldChildren)
          }, item.title)}{item.explanation && <p className={classes(textBlock({}))}>{item.explanation}</p>}</span></li>;
      $[9] = headingLevel;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = answerItems.map(t3);
    $[6] = answerItems;
    $[7] = headingLevel;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[11] !== attributes || $[12] !== t1 || $[13] !== t2) {
    t3 = <ul {...attributes} className={t1}>{t2}</ul>;
    $[11] = attributes;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};